import { httpClient } from '@/core/api/httpClient';
import { env } from '@/core/config/env';
import { API_V1 } from '@/core/api/endpoints';
import { mockChecklistsByTask } from './checklists.mock';
import type {
  AddChecklistItemPayload,
  Checklist,
  CreateChecklistPayload,
  CreatedResponse,
  MoveChecklistItemPayload,
  UpdateChecklistItemPayload,
  UpdateChecklistPayload,
} from '../types';

const USE_MOCK = env.VITE_USE_MOCK === 'true';

/** In-memory mock store (deep copy so mutations don't leak into the source). */
const mockStore: Record<string, Checklist[]> = structuredClone(mockChecklistsByTask);

let mockSeq = 9000;
const nextId = () => String(++mockSeq);

function recount(checklist: Checklist): void {
  checklist.totalItems = checklist.items.length;
  checklist.checkedItems = checklist.items.filter((i) => i.isChecked).length;
  checklist.progress =
    checklist.totalItems === 0 ? 0 : (checklist.checkedItems / checklist.totalItems) * 100;
}

function findItem(itemId: string): { checklist: Checklist; index: number } | null {
  for (const checklists of Object.values(mockStore)) {
    for (const checklist of checklists) {
      const index = checklist.items.findIndex((i) => i.id === itemId);
      if (index !== -1) return { checklist, index };
    }
  }
  return null;
}

function findChecklist(checklistId: string): Checklist | null {
  for (const checklists of Object.values(mockStore)) {
    const found = checklists.find((c) => c.id === checklistId);
    if (found) return found;
  }
  return null;
}

export const checklistsService = {
  getByTask: async (taskId: string, signal?: AbortSignal) => {
    if (USE_MOCK) {
      const data = (mockStore[taskId] ?? []).slice().sort((a, b) => a.order - b.order);
      return Promise.resolve({ data });
    }
    return signal
      ? httpClient.get<Checklist[]>(`${API_V1}/tasks/${taskId}/checklists`, { signal })
      : httpClient.get<Checklist[]>(`${API_V1}/tasks/${taskId}/checklists`);
  },

  create: async (taskId: string, data: CreateChecklistPayload) => {
    if (USE_MOCK) {
      const list = (mockStore[taskId] ??= []);
      const checklist: Checklist = {
        id: nextId(),
        taskId,
        name: data.name,
        order: list.length,
        totalItems: 0,
        checkedItems: 0,
        progress: 0,
        items: [],
        createdAt: new Date().toISOString(),
      };
      list.push(checklist);
      return Promise.resolve({ data: { id: checklist.id } as CreatedResponse });
    }
    return httpClient.post<CreateChecklistPayload, CreatedResponse>(
      `${API_V1}/tasks/${taskId}/checklists`,
      data,
    );
  },

  update: async (checklistId: string, data: UpdateChecklistPayload) => {
    if (USE_MOCK) {
      const checklist = findChecklist(checklistId);
      if (checklist) {
        if (data.name !== undefined) checklist.name = data.name;
        if (data.order !== undefined) checklist.order = data.order;
      }
      return Promise.resolve({ data: undefined as void });
    }
    return httpClient.patch<UpdateChecklistPayload, void>(
      `${API_V1}/checklists/${checklistId}`,
      data,
    );
  },

  delete: async (checklistId: string) => {
    if (USE_MOCK) {
      for (const checklists of Object.values(mockStore)) {
        const index = checklists.findIndex((c) => c.id === checklistId);
        if (index !== -1) checklists.splice(index, 1);
      }
      return Promise.resolve({ data: undefined as void });
    }
    return httpClient.delete<void>(`${API_V1}/checklists/${checklistId}`);
  },
};

export const checklistItemsService = {
  add: async (checklistId: string, data: AddChecklistItemPayload) => {
    if (USE_MOCK) {
      const checklist = findChecklist(checklistId);
      if (checklist) {
        checklist.items.push({
          id: nextId(),
          checklistId,
          title: data.title,
          isChecked: false,
          order: checklist.items.length,
          createdAt: new Date().toISOString(),
          updatedAt: null,
        });
        recount(checklist);
      }
      return Promise.resolve({ data: { id: nextId() } as CreatedResponse });
    }
    return httpClient.post<AddChecklistItemPayload, CreatedResponse>(
      `${API_V1}/checklists/${checklistId}/items`,
      data,
    );
  },

  update: async (itemId: string, data: UpdateChecklistItemPayload) => {
    if (USE_MOCK) {
      const found = findItem(itemId);
      if (found) {
        const item = found.checklist.items[found.index];
        if (data.title !== undefined) item.title = data.title;
        if (data.isChecked !== undefined) item.isChecked = data.isChecked;
        item.updatedAt = new Date().toISOString();
        recount(found.checklist);
      }
      return Promise.resolve({ data: undefined as void });
    }
    return httpClient.patch<UpdateChecklistItemPayload, void>(
      `${API_V1}/checklists/items/${itemId}`,
      data,
    );
  },

  delete: async (itemId: string) => {
    if (USE_MOCK) {
      const found = findItem(itemId);
      if (found) {
        found.checklist.items.splice(found.index, 1);
        recount(found.checklist);
      }
      return Promise.resolve({ data: undefined as void });
    }
    return httpClient.delete<void>(`${API_V1}/checklists/items/${itemId}`);
  },

  move: async (itemId: string, data: MoveChecklistItemPayload) => {
    if (USE_MOCK) {
      const found = findItem(itemId);
      const target = findChecklist(data.targetChecklistId);
      if (found && target) {
        const [item] = found.checklist.items.splice(found.index, 1);
        item.checklistId = target.id;
        target.items.splice(data.order, 0, item);
        recount(found.checklist);
        recount(target);
      }
      return Promise.resolve({ data: undefined as void });
    }
    return httpClient.post<MoveChecklistItemPayload, void>(
      `${API_V1}/checklists/items/${itemId}/move`,
      data,
    );
  },

  convertToTask: async (itemId: string) => {
    if (USE_MOCK) {
      const found = findItem(itemId);
      if (found) {
        found.checklist.items.splice(found.index, 1);
        recount(found.checklist);
      }
      return Promise.resolve({ data: { id: nextId() } as CreatedResponse });
    }
    return httpClient.post<undefined, CreatedResponse>(
      `${API_V1}/checklists/items/${itemId}/convert-to-task`,
      undefined,
    );
  },

  convertToSubtask: async (itemId: string) => {
    if (USE_MOCK) {
      const found = findItem(itemId);
      if (found) {
        found.checklist.items.splice(found.index, 1);
        recount(found.checklist);
      }
      return Promise.resolve({ data: { id: nextId() } as CreatedResponse });
    }
    return httpClient.post<undefined, CreatedResponse>(
      `${API_V1}/checklists/items/${itemId}/convert-to-subtask`,
      undefined,
    );
  },
};
